import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { QrCode, Code, Copy } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Tap2VerifyWidgetAdmin() {
  const [widgets, setWidgets] = useState([]);
  const [form, setForm] = useState({ brand: "", brand_slug: "", accent_color: "#0F172A", language: "en", button_label: "Report a counterfeit" });
  const [generated, setGenerated] = useState(null);

  const load = () => api.get("/tap2verify-widgets").then(r => setWidgets(r.data || []));
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    const r = await api.post("/tap2verify-widget/generate", form);
    setGenerated(r.data);
    load();
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); alert("Copied!"); };

  return (
    <div className="p-6 space-y-4" data-testid="t2v-widget-admin">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><QrCode size={12}/> Tap2Verify widget generator</div>
        <h1 className="font-display font-bold text-3xl mt-1">Embeddable widget &amp; QR for client websites</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">Generate a floating "Report" button (JavaScript) or printable QR code for any brand. Reports come tagged with <code>source='widget'</code>.</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <form onSubmit={generate} className="card-flat p-5 space-y-3 text-[12px]" data-testid="widget-form">
          <div className="font-display font-bold">Generate new widget</div>
          <input data-testid="widget-brand" required placeholder="Brand name (e.g. Bajaj)" value={form.brand} onChange={e=>setForm({...form, brand:e.target.value})} className="w-full hairline px-2 py-1.5"/>
          <input data-testid="widget-slug" placeholder="Slug (auto from brand if blank)" value={form.brand_slug} onChange={e=>setForm({...form, brand_slug:e.target.value})} className="w-full hairline px-2 py-1.5"/>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-eyebrow">Accent</label>
              <input data-testid="widget-color" type="color" value={form.accent_color} onChange={e=>setForm({...form, accent_color:e.target.value})} className="w-full hairline px-1 py-1 h-9"/>
            </div>
            <div>
              <label className="label-eyebrow">Language</label>
              <select data-testid="widget-lang" value={form.language} onChange={e=>setForm({...form, language:e.target.value})} className="w-full hairline px-2 py-1.5">
                {["en","hi","bn","ar"].map(l=><option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <input data-testid="widget-label" placeholder="Button label" value={form.button_label} onChange={e=>setForm({...form, button_label:e.target.value})} className="w-full hairline px-2 py-1.5"/>
          <button data-testid="widget-generate" type="submit" className="span-btn">Generate widget</button>
        </form>

        {generated && (
          <div className="card-flat p-5 space-y-3 text-[12px]" data-testid="widget-output">
            <div className="font-display font-bold">Your widget for <span className="font-mono">{generated.brand}</span></div>
            <div>
              <div className="label-eyebrow mb-1">Embed snippet</div>
              <div className="hairline p-2 bg-[var(--span-surface-2)] font-mono text-[10px] break-all">{generated.embed_snippet}</div>
              <button onClick={() => copy(generated.embed_snippet)} className="mt-1 text-[10px] hairline px-2 py-1"><Copy size={10} className="inline mr-1"/>Copy</button>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Report URL</div>
              <a href={generated.report_url} target="_blank" rel="noopener" className="text-[var(--span-accent)] underline break-all text-[11px]">{generated.report_url}</a>
            </div>
            <div>
              <div className="label-eyebrow mb-1">QR code (for offline print)</div>
              <img src={generated.qr_url} alt="QR" className="w-40 h-40 border" data-testid="widget-qr"/>
              <a href={generated.qr_url} download={`${generated.brand_slug}_qr.png`} className="block mt-1 text-[10px] hairline px-2 py-1 inline-block">Download QR PNG</a>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="font-display font-bold text-[13px] mb-2">Existing widgets</div>
        <div className="card-flat p-0 overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-[var(--span-surface-3)] text-[10px] uppercase">
              <tr><th className="text-left px-3 py-2">Brand</th><th className="text-left px-3 py-2">Slug</th><th className="text-left px-3 py-2">Language</th><th className="text-left px-3 py-2">Created</th><th></th></tr>
            </thead>
            <tbody>
              {widgets.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--span-text-2)]">No widgets yet.</td></tr>}
              {widgets.map(w => (
                <tr key={w.widget_id} className="border-t hairline" data-testid={`widget-row-${w.brand_slug}`}>
                  <td className="px-3 py-2 font-bold">{w.brand}</td>
                  <td className="px-3 py-2 font-mono">{w.brand_slug}</td>
                  <td className="px-3 py-2">{w.language?.toUpperCase()}</td>
                  <td className="px-3 py-2 text-[var(--span-text-2)]">{w.created_at?.substring(0,10)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <a href={`${BASE}/api/tap2verify-widget/${w.brand_slug}.qr.png`} target="_blank" rel="noopener" className="text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)] mr-1">QR</a>
                    <a href={`${BASE}/api/tap2verify-widget/${w.brand_slug}.js`} target="_blank" rel="noopener" className="text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]">JS</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
